import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  CircularProgress,
  TextField,
  Tooltip,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PageLayout from "../../components/layout/PageLayout";
import Can from "../../components/permissions/Can";
import { useGetPaymentErrorsQuery } from "../../store/api";
import { extractPagination } from "../../utils/response";
import { toast } from "react-toastify";
import { buildPaymentErrorRelationParams } from "./paymentErrorApiHelpers";
import {
  // getInvoiceCustomId,
  // getAuthorName,
  getLeadName,
  getErrorMessage,
  formatCreatedAt,
} from "./paymentErrorRowMappers";

const PaymentErrorList = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
  });
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      per_page: pagination.per_page,
      paginate: 1,
      ...buildPaymentErrorRelationParams(),
      ...(search ? { search } : {}),
    }),
    [pagination.page, pagination.per_page, search],
  );

  const {
    data,
    isFetching: loading,
    error,
  } = useGetPaymentErrorsQuery(queryParams);

  const rows = useMemo(() => {
    const list =
      data?.data?.list ??
      data?.list ??
      (Array.isArray(data?.data) ? data.data : []);
    return Array.isArray(list) ? list : [];
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const pag = extractPagination(data);
    setPagination((p) => ({ ...p, total: pag.total ?? 0 }));
  }, [data]);

  useEffect(() => {
    if (error)
      toast.error(error?.data?.message || "Failed to load payment errors");
  }, [error]);

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  };

  const headSx = { fontWeight: 600, whiteSpace: "nowrap" };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography
          variant="h4"
          sx={{ mb: 3, color: "primary.main", fontWeight: 600 }}
        >
          Payment errors
        </Typography>

        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ overflowX: "auto" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    <TableCell sx={headSx}>ID</TableCell>
                    {/* <TableCell sx={headSx}>Author name</TableCell> */}
                    <TableCell sx={headSx}>Lead name</TableCell>
                    <TableCell sx={headSx}>Error</TableCell>
                    <TableCell sx={headSx}>Created at</TableCell>
                    <TableCell
                      sx={{ ...headSx, width: 72, textAlign: "center" }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No payment errors found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, ind) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() =>
                          row.id && navigate(`/payment-errors/view/${row.id}`)
                        }
                        sx={{ cursor: row.id ? "pointer" : "default" }}
                        aria-label="View payment error"
                      >
                        <TableCell
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.8125rem",
                          }}
                        >
                          {row?.id ?? "-"}
                        </TableCell>
                        {/* <TableCell>{getAuthorName(row)}</TableCell> */}
                        <TableCell>{getLeadName(row)}</TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            title={getErrorMessage(row)}
                          >
                            {getErrorMessage(row)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatCreatedAt(row)}
                        </TableCell>
                        <TableCell
                          onClick={(e) => e.stopPropagation()}
                          sx={{ textAlign: "center" }}
                        >
                          <Can permission="payment-error-view">
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                aria-label="View"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (row.id)
                                    navigate(`/payment-errors/view/${row.id}`);
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Can>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default PaymentErrorList;
